import {
  BLIND_NIL,
  NIL,
  TAKES_BAGS,
  HELPS_TEAM_BID,
  NO_BAGS_NO_HELP,
} from '../utils/constants';

import type {
  InputValue,
  NilSetting,
  Round,
  Names,
  TeamBidsAndMade,
} from '../../types';

// p1Bid, p2Bid, p1Made, p2Made
export function calculateRoundScore(
  bid1: InputValue,
  bid2: InputValue,
  made1: InputValue,
  made2: InputValue,
  nilSetting: NilSetting = HELPS_TEAM_BID,
) {
  const someoneWentNil = isNilBid(bid1) || isNilBid(bid2);
  const teamBid = convertStringInputToNum(bid1) + convertStringInputToNum(bid2);
  const teamMade =
    convertStringInputToNum(made1) + convertStringInputToNum(made2);
  const score = someoneWentNil
    ? nilTeamRoundScore(bid1, bid2, made1, made2, nilSetting)
    : teamRoundScore(teamBid, teamMade);
  return score;
}

function teamRoundScore(teamBid: number, teamMade: number) {
  const gotSet = teamMade < teamBid;
  if (gotSet) {
    return { score: teamBid * -10, bags: 0 };
  } else {
    let bags = teamMade - teamBid;
    return { score: teamBid * 10 + bags, bags };
  }
}

export function nilTeamRoundScore(
  bid1: InputValue,
  bid2: InputValue,
  made1: InputValue,
  made2: InputValue,
  nilSetting: NilSetting = HELPS_TEAM_BID,
) {
  const areP1AndP2ScoredIndependently = nilSetting !== HELPS_TEAM_BID;
  if (!areP1AndP2ScoredIndependently) {
    const bothPlayersWentNil = isNilBid(bid1) && isNilBid(bid2);
    if (bothPlayersWentNil) {
      const areBothNotBlind = bid1 === NIL && bid2 === NIL;
      const isOnlyOnePlayerBlind =
        (bid1 === NIL && bid2 === BLIND_NIL) ||
        (bid1 === BLIND_NIL && bid2 === NIL);
      const bothAreBlind = bid1 === BLIND_NIL && bid2 === BLIND_NIL;
      if (areBothNotBlind) {
        return calculateTeamRoundScoreWithBothNonBlindNil(
          made1,
          made2,
          nilSetting,
        );
      } else if (isOnlyOnePlayerBlind) {
        return calculateScoreForDualNilWithOneBlind(
          bid1,
          bid2,
          made1,
          made2,
          nilSetting,
        );
      } else if (bothAreBlind) {
        return calculateTeamRoundScoreWithBothBlindNil(
          made1,
          made2,
          nilSetting,
        );
      }
    }
    return calculateTeamRoundScoreWithOneNilBidder(
      bid1,
      bid2,
      made1,
      made2,
      nilSetting,
    );
  }

  const p1BidValue =
    convertAchievedBidToScoreValue(bid1) +
    overPenalty(bid1, made1, nilSetting) +
    underPenalty(bid1, made1);

  const p2BidValue =
    convertAchievedBidToScoreValue(bid2) +
    overPenalty(bid2, made2, nilSetting) +
    underPenalty(bid2, made2);

  const bags =
    getPlayerBags(bid1, made1, nilSetting) +
    getPlayerBags(bid2, made2, nilSetting);

  return { score: p1BidValue + p2BidValue, bags };
}

// bag penalty
export function overPenalty(
  bid: InputValue,
  made: InputValue,
  nilSetting: string,
) {
  const shouldBePenalized =
    convertStringInputToNum(bid) < convertStringInputToNum(made);
  if (!shouldBePenalized) {
    return 0;
  }
  const bags = getPlayerBags(bid, made, nilSetting);
  const playerWentNil = isNilBid(bid);
  let penalty = 0;
  if (playerWentNil) {
    // taking away original value, and then subtracting that amount again so that it exists as a penalty
    penalty = -convertAchievedBidToScoreValue(bid) * 2;
    penalty += bags;
    return penalty;
  }
  return bags;
}

// set penalty
export function underPenalty(bid: InputValue, made: InputValue) {
  const playerWentNil = isNilBid(bid);
  const gotSet = convertStringInputToNum(made) < convertStringInputToNum(bid);
  const shouldBePenalized = !playerWentNil && gotSet;
  if (!shouldBePenalized) {
    return 0;
  }
  // taking away original value, and then subtracting that amount again so that it exists as a penalty
  const penalty = -convertAchievedBidToScoreValue(bid) * 2;
  return penalty;
}

/** Nil or Blind Nil. */
export function isNilBid(bid: InputValue | undefined): boolean {
  return bid === NIL || bid === BLIND_NIL;
}

export function getPlayerBags(
  bid: InputValue,
  made: InputValue,
  nilSetting: string,
): number {
  let bags = 0;
  if (convertStringInputToNum(bid) >= convertStringInputToNum(made)) {
    return bags;
  }
  switch (nilSetting) {
    case NO_BAGS_NO_HELP: {
      const didSomeoneGoNil = isNilBid(bid);
      if (!didSomeoneGoNil) {
        bags = convertStringInputToNum(made) - convertStringInputToNum(bid);
      }
      return bags;
    }
    case TAKES_BAGS:
      bags = convertStringInputToNum(made) - convertStringInputToNum(bid);
      return bags;
    default:
      throw new Error(
        'This function should not be called unless setting is BLIND_NIL or TAKES_BAGS',
      );
  }
}

// only gets called if HELPS_TEAM_BID setting
export function calculateTeamRoundScoreWithBothNonBlindNil(
  made1: InputValue,
  made2: InputValue,
  _nilSetting?: string,
) {
  const player1AchievedNil = convertStringInputToNum(made1) === 0;
  const player2AchievedNil = convertStringInputToNum(made2) === 0;
  const onlyOnePlayerAchievedNil =
    (player1AchievedNil && !player2AchievedNil) ||
    (!player1AchievedNil && player2AchievedNil);
  if (player1AchievedNil && player2AchievedNil) {
    return {
      score: 100 + 100,
      bags: 0,
    };
  } else if (onlyOnePlayerAchievedNil) {
    const madeFromPlayerWhoFailedNil = player1AchievedNil ? made2 : made1;
    const bags = convertStringInputToNum(madeFromPlayerWhoFailedNil);
    return {
      score: 100 + -100 + bags,
      bags,
    };
  } else {
    const bags =
      convertStringInputToNum(made1) + convertStringInputToNum(made2);
    return {
      score: -100 + -100 + bags,
      bags,
    };
  }
}

// only gets called if HELPS_TEAM_BID setting
export function calculateScoreForDualNilWithOneBlind(
  bid1: InputValue,
  bid2: InputValue,
  made1: InputValue,
  made2: InputValue,
  _nilSetting?: string,
) {
  const { nilPlayerMade, blindNilPlayerMade } = whoWasBlind(
    bid1,
    bid2,
    made1,
    made2,
  );
  const nilPlayerAchievedNil = convertStringInputToNum(nilPlayerMade) === 0;
  const blindNilPlayerAchievedNil =
    convertStringInputToNum(blindNilPlayerMade) === 0;
  if (nilPlayerAchievedNil && blindNilPlayerAchievedNil) {
    return {
      score: 100 + 200,
      bags: 0,
    };
  } else if (nilPlayerAchievedNil && !blindNilPlayerAchievedNil) {
    const bags = convertStringInputToNum(blindNilPlayerMade);
    return {
      score: 100 + -200 + bags,
      bags,
    };
  } else if (!nilPlayerAchievedNil && blindNilPlayerAchievedNil) {
    const bags = convertStringInputToNum(nilPlayerMade);
    return {
      score: -100 + 200 + bags,
      bags,
    };
  } else {
    const bags =
      convertStringInputToNum(nilPlayerMade) +
      convertStringInputToNum(blindNilPlayerMade);
    return {
      score: -100 + -200 + bags,
      bags,
    };
  }
}

// only gets called if HELPS_TEAM_BID setting
export function calculateTeamRoundScoreWithBothBlindNil(
  made1: InputValue,
  made2: InputValue,
  _nilSetting?: string,
) {
  const player1AchievedNil = convertStringInputToNum(made1) === 0;
  const player2AchievedNil = convertStringInputToNum(made2) === 0;
  const onlyOnePlayerAchievedNil =
    (player1AchievedNil && !player2AchievedNil) ||
    (!player1AchievedNil && player2AchievedNil);
  if (player1AchievedNil && player2AchievedNil) {
    return {
      score: 200 + 200,
      bags: 0,
    };
  } else if (onlyOnePlayerAchievedNil) {
    const madeFromPlayerWhoFailedNil = player1AchievedNil ? made2 : made1;
    const bags = convertStringInputToNum(madeFromPlayerWhoFailedNil);
    return {
      score: 200 + -200 + bags,
      bags,
    };
  } else {
    const bags =
      convertStringInputToNum(made1) + convertStringInputToNum(made2);
    return {
      score: -200 + -200 + bags,
      bags,
    };
  }
}

// only gets called with HELPS_TEAM_BID setting
export function calculateTeamRoundScoreWithOneNilBidder(
  bid1: InputValue,
  bid2: InputValue,
  made1: InputValue,
  made2: InputValue,
  _nilSetting?: string,
) {
  const { nilPlayerBid, nonNilPlayerBid, nilPlayerMade } = whoWentNil(
    bid1,
    bid2,
    made1,
    made2,
  );
  const wasBlind = nilPlayerBid === BLIND_NIL;
  const achievedNil = convertStringInputToNum(nilPlayerMade) === 0;
  const totalMade =
    convertStringInputToNum(made1) + convertStringInputToNum(made2);
  const didntGetSet = totalMade >= convertStringInputToNum(nonNilPlayerBid);
  const bags =
    totalMade > convertStringInputToNum(nonNilPlayerBid)
      ? totalMade - convertStringInputToNum(nonNilPlayerBid)
      : 0;
  const nonNilPlayerScore = didntGetSet
    ? convertStringInputToNum(nonNilPlayerBid) * 10 + bags
    : -convertStringInputToNum(nonNilPlayerBid) * 10;

  const nilScore = wasBlind ? 200 : 100;
  const nilMultiplier = achievedNil ? 1 : -1;
  return {
    score: nilMultiplier * nilScore + nonNilPlayerScore,
    bags,
  };
}

// consider renaming.
// also, this function assumes only one bid is NIL. that might also be a code smell. Consider refactoring into class for encapsulation
export function whoWentNil(
  bid1: InputValue,
  bid2: InputValue,
  made1: InputValue,
  made2: InputValue,
) {
  const nilPlayerBid = [bid1, bid2].find((bid) => isNilBid(bid));
  const nonNilPlayerBid = [bid1, bid2].find((bid) => !isNilBid(bid));
  const nilPlayerMade = bid1 === nilPlayerBid ? made1 : made2;
  const nonNilPlayerMade = bid1 === nonNilPlayerBid ? made1 : made2;

  return {
    nilPlayerBid,
    nonNilPlayerBid,
    nilPlayerMade,
    nonNilPlayerMade,
  };
}

// this function assumes both bidders went Nil but only one went blind. that might also be a code smell. Consider refactoring into class for encapsulation
export function whoWasBlind(
  bid1: InputValue,
  bid2: InputValue,
  made1: InputValue,
  made2: InputValue,
) {
  const nilPlayerBid = [bid1, bid2].find((bid) => bid === NIL);
  const blindNilPlayerBid = [bid1, bid2].find((bid) => bid === BLIND_NIL);
  const nilPlayerMade = bid1 === nilPlayerBid ? made1 : made2;
  const blindNilPlayerMade = bid1 === blindNilPlayerBid ? made1 : made2;

  return {
    nilPlayerMade,
    blindNilPlayerMade,
  };
}

export function calculateTeamRoundScoresFromTeamHistory(
  teamHistory: TeamBidsAndMade[],
  nilSetting: NilSetting | null,
) {
  return teamHistory
    .filter((team): team is TeamBidsAndMade => team != null)
    .map((team) => {
      const teamRoundScore = calculateRoundScore(
        team.p1Bid,
        team.p2Bid,
        team.p1Made,
        team.p2Made,
        nilSetting || HELPS_TEAM_BID,
      );
      return {
        teamScore: teamRoundScore.score,
        teamBags: teamRoundScore.bags,
      };
    });
}

export function getDealerIdHistory(
  roundHistory: Round[],
  firstDealerOrder: string[],
) {
  const dealerIdHistory: string[] = [];
  let currentDealerOrder = [...firstDealerOrder];

  roundHistory.forEach((round) => {
    // Check if there's a dealer override for this round
    if (round?.dealerOverride) {
      dealerIdHistory.push(round.dealerOverride);
    } else {
      // Use natural dealer rotation based on the current dealer order
      dealerIdHistory.push(currentDealerOrder[0]);
    }

    // Update the dealer order for the next round based on who actually dealt
    // This should be the dealer that was actually used for this round
    const actualDealer = round?.dealerOverride || currentDealerOrder[0];

    // Find the next dealer in the rotation after the actual dealer
    const actualDealerIndex = currentDealerOrder.indexOf(actualDealer);
    if (actualDealerIndex !== -1) {
      // Rotate the dealer order so that the next dealer comes first
      const nextDealerIndex =
        (actualDealerIndex + 1) % currentDealerOrder.length;
      const rotatedOrder = [];

      // Start from the next dealer and go around
      for (let i = 0; i < currentDealerOrder.length; i++) {
        const index = (nextDealerIndex + i) % currentDealerOrder.length;
        rotatedOrder.push(currentDealerOrder[index]);
      }

      currentDealerOrder = rotatedOrder;
    }
  });

  return dealerIdHistory;
}

interface GetCurrentDealerIdArgs {
  dealerIdHistory: string[];
  index: number;
  isCurrent: boolean;
  firstDealerOrder: string[];
  dealerOverride?: string | null;
  roundHistory?: Round[];
}

export function getCurrentDealerId({
  dealerIdHistory,
  index,
  isCurrent,
  firstDealerOrder,
  dealerOverride = null,
  roundHistory = [],
}: GetCurrentDealerIdArgs) {
  // If there's a dealer override for the current round, use it
  if (isCurrent && dealerOverride) {
    return dealerOverride;
  }

  // If there's a dealer override for a past round, use it
  if (!isCurrent && roundHistory[index]?.dealerOverride) {
    return roundHistory[index].dealerOverride;
  }

  if (isCurrent) {
    if (dealerIdHistory.length === 0) {
      return firstDealerOrder[0];
    }
    // For current round, calculate the next dealer based on the last dealer in history
    const lastDealer = dealerIdHistory[dealerIdHistory.length - 1];
    const lastDealerIndex = firstDealerOrder.indexOf(lastDealer);
    const nextDealerIndex = (lastDealerIndex + 1) % firstDealerOrder.length;
    return firstDealerOrder[nextDealerIndex];
  } else {
    // For past rounds, use the dealer from the dealerIdHistory array
    if (index < dealerIdHistory.length) {
      return dealerIdHistory[index];
    }
    // If we don't have enough history, fall back to the original logic
    if (index < 4) {
      return firstDealerOrder[index];
    }
    return dealerIdHistory[index - 4];
  }
}

export function getTeamHistoryFromRoundHistory(
  roundHistory: Round[],
  teamBidsAndMade: 'team1BidsAndMade' | 'team2BidsAndMade',
): TeamBidsAndMade[] {
  return roundHistory
    .filter((round): round is Round => round != null)
    .map((round) => round[teamBidsAndMade])
    .filter((team): team is TeamBidsAndMade => team != null);
}

export function calculateTeamScoreFromRoundHistory(
  roundHistory: Round[],
  teamBidsAndMade: 'team1BidsAndMade' | 'team2BidsAndMade',
  nilSetting: NilSetting | null,
) {
  const teamRoundHistory = getTeamHistoryFromRoundHistory(
    roundHistory,
    teamBidsAndMade,
  );
  const roundScores = calculateTeamRoundScoresFromTeamHistory(
    teamRoundHistory,
    nilSetting,
  );

  const initialScore = {
    teamScore: 0,
    teamBags: 0,
  };

  const gameScore = roundScores.reduce(addRounds, initialScore);
  return gameScore;
}

function addRounds(
  prev: { teamScore: number; teamBags: number },
  roundScore: { teamScore: number; teamBags: number },
) {
  let teamScore = prev.teamScore + roundScore.teamScore;
  let teamBags = prev.teamBags + roundScore.teamBags;
  if (teamBags >= 10) {
    teamScore -= 100;
    teamBags -= 10;
  }
  return { teamScore, teamBags };
}

export function convertAchievedBidToScoreValue(input: InputValue | undefined) {
  if (input === undefined || input === '') return 0;
  switch (input) {
    case BLIND_NIL:
      return 200;
    case NIL:
      return 100;
    default:
      return (typeof input === 'number' ? input : parseInt(input, 10)) * 10;
  }
}

export function convertStringInputToNum(input: InputValue | undefined) {
  if (input === undefined) return 0;
  switch (input) {
    case BLIND_NIL:
    case NIL:
    case '':
      return 0;
    default:
      return typeof input === 'number' ? input : parseInt(input, 10);
  }
}

export function addInputs(...inputs: InputValue[]) {
  return inputs.reduce((acc: number, input) => {
    return acc + convertStringInputToNum(input);
  }, 0);
}

export const isNotDefaultValue = (value: InputValue) => {
  return value !== '';
};

export const hasPlayerNamesEntered = (names: Names | null) => {
  if (!names) return false;
  const playerFields: (keyof Names)[] = [
    't1p1Name',
    't1p2Name',
    't2p1Name',
    't2p2Name',
  ];
  const hasPlayerNames = playerFields.some(
    (field) => names[field] && names[field] !== '',
  );
  const hasCustomTeamNames =
    (names.team1Name && names.team1Name !== 'Team 1') ||
    (names.team2Name && names.team2Name !== 'Team 2');
  return hasPlayerNames || hasCustomTeamNames;
};

export const hasRoundProgress = (
  roundHistory: Round[],
  currentRound: Round | null,
) => {
  if (roundHistory.length > 0) return true;
  if (!currentRound) return false;

  const hasTeam1Progress = Object.values(currentRound.team1BidsAndMade).some(
    isNotDefaultValue,
  );
  const hasTeam2Progress = Object.values(currentRound.team2BidsAndMade).some(
    isNotDefaultValue,
  );

  return hasTeam1Progress || hasTeam2Progress;
};

/*
A round only counts as finished once every bid and made value is entered AND the
made add up to 13 — the same bar useIndependentTeamScoring uses before a
round is moved into history.
*/
export const isRoundComplete = (round: Round | null): boolean => {
  if (!round?.team1BidsAndMade || !round.team2BidsAndMade) {
    return false;
  }
  const { team1BidsAndMade, team2BidsAndMade } = round;

  const allInputsEntered = [
    ...Object.values(team1BidsAndMade),
    ...Object.values(team2BidsAndMade),
  ].every(isNotDefaultValue);
  if (!allInputsEntered) return false;

  const totalMade = addInputs(
    team1BidsAndMade.p1Made,
    team1BidsAndMade.p2Made,
    team2BidsAndMade.p1Made,
    team2BidsAndMade.p2Made,
  );

  return totalMade === 13;
};

/*
Determines the end-game outcome from finished scores. A team "hits" the limit
at or above it; if both teams hit in the same completed round, the higher
score takes the game and an exact tie goes to overtime (tieAt).
*/
export const getGameOutcome = (
  team1: number,
  team2: number,
  scoreLimit: number | null,
): { winner: 'team1' | 'team2' | null; tieAt: number | null } => {
  if (scoreLimit == null) return { winner: null, tieAt: null };
  const team1Hit = team1 >= scoreLimit;
  const team2Hit = team2 >= scoreLimit;
  if (!team1Hit && !team2Hit) return { winner: null, tieAt: null };
  if (team1Hit && team2Hit) {
    if (team1 > team2) return { winner: 'team1', tieAt: null };
    if (team2 > team1) return { winner: 'team2', tieAt: null };
    return { winner: null, tieAt: team1 };
  }
  return { winner: team1Hit ? 'team1' : 'team2', tieAt: null };
};
